import React, { useState, useRef, KeyboardEvent, ClipboardEvent, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import { Mail, Lock, EyeOff, Eye, Loader2, AlertCircle, ArrowLeft, CheckCircle } from "lucide-react";
import { authService } from "../services/authService";
import { websocketService } from "../services/websocketService";
import logo from "../assets/whitelogo.svg";
import { useAuth } from "../App";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [error, setError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [resetStep, setResetStep] = useState(1); // 1: Request code, 2: Enter code & new password, 3: Success
  const [verificationCodes, setVerificationCodes] = useState<string[]>(Array(6).fill(''));
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [codeError, setCodeError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const codeInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Handle hash-based mode switching
  useEffect(() => {
    const checkHashMode = () => {
      const hash = window.location.hash;
      if (hash === '#signup') {
        setIsLogin(false);
      } else if (hash === '#login' || hash === '') {
        setIsLogin(true);
      }
    };

    // Check hash on mount
    checkHashMode();

    // Listen for hash changes
    const handleHashChange = () => {
      checkHashMode();
    };

    window.addEventListener('hashchange', handleHashChange);

    // Cleanup
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  // Update URL hash when mode changes
  useEffect(() => {
    const currentHash = window.location.hash;
    const expectedHash = isLogin ? '#login' : '#signup';
    
    if (currentHash !== expectedHash) {
      // Update hash without triggering a page reload
      window.history.replaceState(null, '', window.location.pathname + expectedHash);
    }
  }, [isLogin]);

  // Email validation function
  const validateEmail = (email: string) => {
    // Basic email regex that ensures @ symbol and a domain with at least one dot
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Password validation function
  const validatePassword = (password: string) => {
    return password.length >= 8; // Simple validation: at least 8 characters
  };

  const handleSubmit = async (e: React.FormEvent) => {
    if (e) {
      e.preventDefault(); // Prevent page reload
      e.stopPropagation(); // Stop event propagation
    }
    
    setError("");
    setEmailError("");
    
    // Trim email and validate format
    const trimmedEmail = email.trim();
    if (!validateEmail(trimmedEmail)) {
      setEmailError("Please enter a valid email address (e.g., example@domain.com)");
      return; // Stop form submission
    }
    
    setIsLoading(true);

    try {
      if (isLogin) {
        console.log("Attempting login with:", { email: trimmedEmail });
        // Trim spaces from password
        const trimmedPassword = password.trim();
        const response = await authService.login(trimmedEmail, trimmedPassword);
        console.log("Login successful:", response);
        
        // Update auth context state after successful login
        await checkAuth();
        console.log("Auth state updated");
        
        // Check and log API onboarding status with strict check for boolean value
        console.log("API onboarding_complete raw value:", response.onboarding_complete);
        
        // Using strict comparison to check if onboarding_complete is truly boolean true
        // We need to handle cases where it might be a string "true" or other truthy value
        const isOnboardingComplete = response.onboarding_complete === true;
        console.log("Is onboarding complete (forced boolean):", isOnboardingComplete);
        
        // Store onboarding status in localStorage for future checks
        localStorage.setItem("onboarding_complete", isOnboardingComplete ? "true" : "false");
        console.log("Stored onboarding status in localStorage:", isOnboardingComplete ? "true" : "false");
        
        // Dispatch custom event to notify listeners of localStorage change
        window.dispatchEvent(new Event('localStorageUpdated'));
        
        // Set a clear destination based on onboarding status to prevent flickering
        const destination = isOnboardingComplete ? "/mynotes" : "/doctor-onboarding";
        console.log(`Navigating directly to ${destination}`);
        
        // WebSocket connection will be handled by AuthContext after checkAuth() is called
        
        // Navigate with replace to avoid history build-up that can cause back-button issues
        console.log(`Final navigation: User ${isOnboardingComplete ? "HAS" : "HAS NOT"} completed onboarding, redirecting to ${destination}`);
        navigate(destination, { replace: true });
      } else {
        console.log("Attempting signup with:", { email: trimmedEmail });
        // Trim spaces from password
        const trimmedPassword = password.trim();
        await authService.signup(trimmedEmail, trimmedPassword);
        console.log("Signup successful");
        
        // Update auth context state after successful signup
        await checkAuth();
        console.log("Auth state updated after signup");
        
        // New users always need to complete onboarding - navigate with replace
        console.log("Navigating directly to /doctor-onboarding after signup");
        navigate("/doctor-onboarding", { replace: true });
      }
    } catch (err: any) {
      console.error("Authentication error:", err);
      
      // Simplified error handling - directly use API error message
      if (err.response) {
        if (err.response.status === 401) {
          setError("Invalid username and password");
        } else if (err.response.data) {
          // Get error message directly from API response
          const errorData = err.response.data;
          
          if (typeof errorData === 'string') {
            setError(errorData);
          } else if (errorData.detail) {
            // Handle detail property if it exists
            setError(typeof errorData.detail === 'string' ? errorData.detail : JSON.stringify(errorData.detail));
          } else {
            // Just stringify whatever we got
            setError(JSON.stringify(errorData));
          }
        }
      } else if (err.message) {
        // Simple error message for network errors
        setError(err.message);
      } else {
        // Fallback for unexpected error format
        setError("An error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    if (e) {
      e.preventDefault(); // Prevent page reload
      e.stopPropagation(); // Stop event propagation
    }
    
    setError("");
    setEmailError("");
    setSuccessMessage("");
    
    // Trim email and validate format
    const trimmedEmail = email.trim();
    if (!validateEmail(trimmedEmail)) {
      setEmailError("Please enter a valid email address (e.g., example@domain.com)");
      return; // Stop form submission
    }
    
    setIsLoading(true);
    
    try {
      const response = await authService.requestPasswordReset(trimmedEmail);
      console.log("Password reset requested successfully:", response);
      setSuccessMessage("Verification code sent! Please check your email.");
      setResetStep(2); // Move to the next step
    } catch (err: any) {
      console.error("Password reset request error:", err);
      setError(err.message || "Failed to request password reset. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Function to handle input change in verification code boxes
  const handleCodeChange = (index: number, value: string) => {
    // Only allow digits
    if (!/^\d*$/.test(value)) return;
    
    // Create a copy of the current codes
    const newCodes = [...verificationCodes];
    // Update the value at the specified index (only keep the last character if multiple)
    newCodes[index] = value.slice(-1);
    setVerificationCodes(newCodes);
    setCodeError("");
    
    // Auto-advance to next input if a digit was entered
    if (value !== '' && index < 5) {
      codeInputRefs.current[index + 1]?.focus();
    }
  };

  // Handle backspace to go to previous input
  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && index > 0 && verificationCodes[index] === '') {
      codeInputRefs.current[index - 1]?.focus();
    }
  };

  // Handle paste event to fill multiple boxes at once
  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    if (!/^\d+$/.test(pastedData)) return; // Only allow digits
    
    const digits = pastedData.slice(0, 6).split('');
    const newCodes = [...verificationCodes];
    
    digits.forEach((digit, index) => {
      if (index < 6) {
        newCodes[index] = digit;
      }
    });
    
    setVerificationCodes(newCodes);
    
    // Focus the box after the last pasted digit
    if (digits.length < 6) {
      codeInputRefs.current[digits.length]?.focus();
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    if (e) {
      e.preventDefault(); // Prevent page reload
      e.stopPropagation(); // Stop event propagation
    }
    
    setError("");
    setCodeError("");
    setPasswordError("");
    setSuccessMessage("");
    
    // Combine verification code digits and validate
    const combinedCode = verificationCodes.join('');
    if (combinedCode.length !== 6) {
      setCodeError("Please enter the complete 6-digit verification code");
      return;
    }
    
    // Validate new password
    if (!validatePassword(newPassword)) {
      setPasswordError("Password must be at least 8 characters long");
      return;
    }
    
    // Validate password confirmation
    if (newPassword !== confirmPassword) {
      setPasswordError("Passwords do not match");
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await authService.resetPassword(email.trim(), combinedCode, newPassword);
      console.log("Password reset successful:", response);
      setSuccessMessage("Your password has been reset successfully!");
      setResetStep(3); // Move to success step
    } catch (err: any) {
      console.error("Password reset error:", err);
      setError(err.message || "Failed to reset password. Please check your code and try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const resetPasswordFlow = () => {
    // Reset all state
    setError("");
    setEmailError("");
    setCodeError("");
    setPasswordError("");
    setSuccessMessage("");
    setVerificationCodes(Array(6).fill(''));
    setNewPassword("");
    setConfirmPassword("");
    setResetStep(1);
  };

  const returnToLogin = () => {
    // Reset all password reset related state
    resetPasswordFlow();
    setShowForgotPassword(false);
  };

  return (
    <div className="h-screen flex overflow-hidden bg-gray-50">
      <a 
        href="https://www.documd.com" 
        rel="noopener noreferrer" 
        className="absolute top-8 left-8 z-20 hover:opacity-80 transition-opacity cursor-pointer"
      >
        <img src={logo} alt="DocuMD" className="h-10 w-auto" />
      </a>

      <div className="hidden lg:flex w-1/2 bg-gradient-to-br from-gray-900 via-gray-950 to-black relative overflow-hidden items-center justify-center">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,_rgba(59,130,246,0.4)_0%,_rgba(0,0,0,0)_70%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_70%,_rgba(59,130,246,0.3)_0%,_rgba(0,0,0,0)_70%)]" />
        <div className="relative z-10 flex flex-col items-center text-center max-w-lg px-8">
          <h2 className="text-7xl font-bold leading-tight tracking-tight text-white mb-6">
            Transform Your
            <br />
            <span className="bg-gradient-to-r from-blue-400 via-blue-500 to-blue-600 bg-clip-text text-transparent">
              Medical Notes
            </span>
          </h2>
          <p className="text-gray-400 text-2xl leading-relaxed">
            Experience the future of medical transcription with our AI-powered
            platform.
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-white via-gray-50 to-blue-50/30" />
        <div className="w-full max-w-md relative">
          {showForgotPassword ? (
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-8 border border-gray-200/50">
              {/* Step 1: Request verification code */}
              {resetStep === 1 && (
                <>
                  <div className="text-center mb-8">
                    <div className="inline-block p-3 bg-blue-50 rounded-xl mb-4">
                      <Mail className="w-8 h-8 text-blue-600" />
                    </div>
                    <h1 className="text-3xl font-bold text-gray-900">
                      Reset Password
                    </h1>
                    <p className="text-gray-600 mt-2 max-w-sm mx-auto">
                      Enter your email and we'll send you a verification code to reset your password
                    </p>
                  </div>

                  <form onSubmit={handleForgotPassword} className="space-y-6" noValidate>
                    {error && (
                      <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
                        {error}
                      </div>
                    )}
                    
                    {successMessage && (
                      <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-green-600 text-sm">
                        {successMessage}
                      </div>
                    )}
                    
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-gray-700">
                        Email Address
                      </label>
                      <div className="relative group">
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => {
                            // Remove spaces from email as user types
                            setEmail(e.target.value.replace(/\s/g, ''));
                            setEmailError("");
                          }}
                          className={`w-full px-4 py-3 rounded-xl bg-white border ${
                            emailError 
                              ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-500/20" 
                              : "border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20"
                          } transition-all outline-none pl-11 group-hover:border-gray-300`}
                          placeholder="Enter your email"
                          required
                        />
                        <Mail className={`w-5 h-5 ${emailError ? "text-red-400" : "text-gray-400"} absolute left-4 top-1/2 -translate-y-1/2 group-hover:text-gray-500 transition-colors`} />
                      </div>
                      {emailError && (
                        <div className="flex items-center mt-1 text-xs text-red-600">
                          <AlertCircle className="w-3.5 h-3.5 mr-1 flex-shrink-0" />
                          {emailError}
                        </div>
                      )}
                    </div>

                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition-all shadow-lg shadow-blue-600/20 hover:shadow-blue-600/30 flex items-center justify-center"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Sending Verification Code...
                        </>
                      ) : (
                        "Send Verification Code"
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={returnToLogin}
                      className="w-full py-3 rounded-xl font-semibold text-gray-700 hover:bg-gray-50 transition-all border border-gray-200 hover:border-gray-300 hover:shadow-lg hover:shadow-gray-200/40 flex items-center justify-center"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back to Sign In
                    </button>
                  </form>
                </>
              )}

              {/* Step 2: Enter verification code and new password */}
              {resetStep === 2 && (
                <>
                  <div className="text-center mb-8">
                    <div className="inline-block p-3 bg-blue-50 rounded-xl mb-4">
                      <Lock className="w-8 h-8 text-blue-600" />
                    </div>
                    <h1 className="text-3xl font-bold text-gray-900">
                      Verify & Reset
                    </h1>
                    <p className="text-gray-600 mt-2 max-w-sm mx-auto">
                      Enter the verification code sent to your email and create a new password
                    </p>
                  </div>

                  <form onSubmit={handleResetPassword} className="space-y-6" noValidate>
                    {error && (
                      <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
                        {error}
                      </div>
                    )}
                    
                    {successMessage && (
                      <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-green-600 text-sm">
                        {successMessage}
                      </div>
                    )}

                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-gray-700">
                        Verification Code
                      </label>
                      <div className="flex justify-between gap-2">
                        {[0, 1, 2, 3, 4, 5].map((index) => (
                          <input
                            key={index}
                            ref={(el) => (codeInputRefs.current[index] = el)}
                            type="text"
                            inputMode="numeric"
                            maxLength={1}
                            value={verificationCodes[index]}
                            onChange={(e) => handleCodeChange(index, e.target.value)}
                            onKeyDown={(e) => handleKeyDown(index, e)}
                            onPaste={index === 0 ? handlePaste : undefined}
                            className={`w-12 h-12 text-center text-xl font-semibold rounded-xl bg-white border ${
                              codeError 
                                ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-500/20" 
                                : "border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20"
                            } transition-all outline-none group-hover:border-gray-300`}
                            required
                          />
                        ))}
                      </div>
                      {codeError && (
                        <div className="flex items-center mt-1 text-xs text-red-600">
                          <AlertCircle className="w-3.5 h-3.5 mr-1 flex-shrink-0" />
                          {codeError}
                        </div>
                      )}
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-gray-700">
                        New Password
                      </label>
                      <div className="relative group">
                        <input
                          type={showNewPassword ? "text" : "password"}
                          value={newPassword}
                          onChange={(e) => {
                            setNewPassword(e.target.value.replace(/\s/g, ''));
                            setPasswordError("");
                          }}
                          className={`w-full px-4 py-3 rounded-xl bg-white border ${
                            passwordError 
                              ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-500/20" 
                              : "border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20"
                          } transition-all outline-none pl-11 pr-11 group-hover:border-gray-300`}
                          placeholder="Create new password"
                          required
                        />
                        <Lock className="w-5 h-5 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2 group-hover:text-gray-500 transition-colors" />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          {showNewPassword ? (
                            <EyeOff className="w-5 h-5" />
                          ) : (
                            <Eye className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-gray-700">
                        Confirm Password
                      </label>
                      <div className="relative group">
                        <input
                          type={showNewPassword ? "text" : "password"}
                          value={confirmPassword}
                          onChange={(e) => {
                            setConfirmPassword(e.target.value.replace(/\s/g, ''));
                            setPasswordError("");
                          }}
                          className={`w-full px-4 py-3 rounded-xl bg-white border ${
                            passwordError 
                              ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-500/20" 
                              : "border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20"
                          } transition-all outline-none pl-11 group-hover:border-gray-300`}
                          placeholder="Confirm new password"
                          required
                        />
                        <Lock className="w-5 h-5 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2 group-hover:text-gray-500 transition-colors" />
                      </div>
                      {passwordError && (
                        <div className="flex items-center mt-1 text-xs text-red-600">
                          <AlertCircle className="w-3.5 h-3.5 mr-1 flex-shrink-0" />
                          {passwordError}
                        </div>
                      )}
                    </div>

                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition-all shadow-lg shadow-blue-600/20 hover:shadow-blue-600/30 flex items-center justify-center"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Resetting Password...
                        </>
                      ) : (
                        "Reset Password"
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={resetPasswordFlow}
                      className="w-full py-3 rounded-xl font-semibold text-gray-700 hover:bg-gray-50 transition-all border border-gray-200 hover:border-gray-300 hover:shadow-lg hover:shadow-gray-200/40 flex items-center justify-center"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back to Request Code
                    </button>
                  </form>
                </>
              )}

              {/* Step 3: Success message */}
              {resetStep === 3 && (
                <>
                  <div className="text-center mb-8">
                    <div className="inline-block p-3 bg-green-50 rounded-xl mb-4">
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    </div>
                    <h1 className="text-3xl font-bold text-gray-900">
                      Password Reset Complete
                    </h1>
                    <p className="text-gray-600 mt-2 max-w-sm mx-auto">
                      Your password has been reset successfully. You can now log in with your new password.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={returnToLogin}
                    className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition-all shadow-lg shadow-blue-600/20 hover:shadow-blue-600/30"
                  >
                    Return to Sign In
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-8 border border-gray-200/50">
              <div className="text-center mb-8">
                <div className="inline-block p-3 bg-blue-50 rounded-xl mb-4">
                  {isLogin ? (
                    <Lock className="w-8 h-8 text-blue-600" />
                  ) : (
                    <Mail className="w-8 h-8 text-blue-600" />
                  )}
                </div>
                <h1 className="text-3xl font-bold text-gray-900">
                  {isLogin ? "Welcome Back" : "Create Account"}
                </h1>
                <p className="text-gray-600 mt-2 max-w-sm mx-auto">
                  {isLogin
                    ? "Sign in to continue to DocuMD"
                    : "Join DocuMD and streamline your documentation"}
                </p>
              </div>

              <form onSubmit={(e) => {e.preventDefault(); return false;}} className="space-y-6" noValidate>
                {error && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
                    {error}
                  </div>
                )}
                {!isLogin && (
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">
                      Full Name
                    </label>
                    <div className="relative group">
                      <input
                        type="text"
                        className="w-full px-4 py-3 rounded-xl bg-white border border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 transition-all outline-none pl-11 group-hover:border-gray-300"
                        placeholder="Enter your full name"
                        required
                      />
                      <Mail className="w-5 h-5 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2 group-hover:text-gray-500 transition-colors" />
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700">
                    Email Address
                  </label>
                  <div className="relative group">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => {
                        // Remove spaces from email as user types
                        setEmail(e.target.value.replace(/\s/g, ''));
                        setEmailError("");
                      }}
                      className={`w-full px-4 py-3 rounded-xl bg-white border ${
                        emailError 
                          ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-500/20" 
                          : "border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20"
                      } transition-all outline-none pl-11 group-hover:border-gray-300`}
                      placeholder="Enter your email"
                      required
                    />
                    <Mail className={`w-5 h-5 ${emailError ? "text-red-400" : "text-gray-400"} absolute left-4 top-1/2 -translate-y-1/2 group-hover:text-gray-500 transition-colors`} />
                  </div>
                  {emailError && (
                    <div className="flex items-center mt-1 text-xs text-red-600">
                      <AlertCircle className="w-3.5 h-3.5 mr-1 flex-shrink-0" />
                      {emailError}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <label className="text-sm font-semibold text-gray-700">
                      Password
                    </label>
                    {isLogin && (
                      <button
                        type="button"
                        onClick={() => setShowForgotPassword(true)}
                        className="text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline"
                      >
                        Forgot Password?
                      </button>
                    )}
                  </div>
                  <div className="relative group">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => {
                        // Remove spaces from password as user types
                        setPassword(e.target.value.replace(/\s/g, ''));
                      }}
                      className="w-full px-4 py-3 rounded-xl bg-white border border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 transition-all outline-none pl-11 pr-11 group-hover:border-gray-300"
                      placeholder={
                        isLogin ? "Enter your password" : "Create a password"
                      }
                      required
                    />
                    <Lock className="w-5 h-5 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2 group-hover:text-gray-500 transition-colors" />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>

                <button
                  type="button" 
                  onClick={handleSubmit}
                  disabled={isLoading}
                  className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition-all shadow-lg shadow-blue-600/20 hover:shadow-blue-600/30 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      {isLogin ? "Signing In..." : "Creating Account..."}
                    </>
                  ) : (
                    <>{isLogin ? "Sign In" : "Create Account"}</>
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => setIsLogin(!isLogin)}
                  disabled={isLoading}
                  className="w-full py-3 rounded-xl font-semibold text-gray-700 hover:bg-gray-50 transition-all border border-gray-200 hover:border-gray-300 hover:shadow-lg hover:shadow-gray-200/40 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isLogin ? "Create New Account" : "Sign In Instead"}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
